import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
// Removed lucide-react icons, using Voice logo instead
import { useVoice } from '../contexts/VoiceContext'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'

interface VoiceInputProps {
  onTranscript?: (transcript: string) => void
  className?: string
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscript, className = '' }) => {
  const { 
    isListening, 
    transcript, 
    isProcessing, 
    isSpeaking, 
    error, 
    startListening, 
    stopListening,
    clearError 
  } = useVoice()
  
  const { isConnected } = useWeb3Wallet()
  const [showWaveform, setShowWaveform] = useState(false)

  useEffect(() => {
    setShowWaveform(isListening || isProcessing)
  }, [isListening, isProcessing])

  useEffect(() => {
    if (transcript && onTranscript) {
      onTranscript(transcript)
    }
  }, [transcript, onTranscript])

  const handleVoiceToggle = () => {
    if (!isConnected) {
      return
    }

    if (error) {
      clearError()
    }

    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
  }

  const getButtonState = () => {
    if (!isConnected) return 'disabled'
    if (error) return 'error'
    if (isProcessing) return 'processing'
    if (isListening) return 'listening'
    if (isSpeaking) return 'speaking'
    return 'idle'
  }

  const buttonState = getButtonState()

  const buttonVariants = {
    idle: {
      scale: 1,
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
    },
    listening: {
      scale: 1.1,
      backgroundColor: 'rgba(239, 68, 68, 0.8)',
      boxShadow: '0 4px 30px rgba(239, 68, 68, 0.5)'
    },
    processing: {
      scale: 1.05,
      backgroundColor: 'rgba(245, 158, 11, 0.8)',
      boxShadow: '0 4px 25px rgba(245, 158, 11, 0.4)'
    },
    speaking: {
      scale: 1.05,
      backgroundColor: 'rgba(34, 197, 94, 0.8)',
      boxShadow: '0 4px 25px rgba(34, 197, 94, 0.4)'
    },
    error: {
      scale: 1,
      backgroundColor: 'rgba(239, 68, 68, 0.8)',
      boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)'
    },
    disabled: {
      scale: 1,
      backgroundColor: 'rgba(107, 114, 128, 0.5)',
      boxShadow: '0 4px 15px rgba(107, 114, 128, 0.2)'
    }
  }

  const getButtonIcon = () => {
    // Always use Voice logo regardless of state
    return <img src="/Voice-32x32.svg" alt="Voice" className="w-8 h-8" />
  }

  const getStatusText = () => {
    switch (buttonState) {
      case 'listening':
        return 'Listening...'
      case 'processing':
        return 'Processing...'
      case 'speaking':
        return 'Speaking...'
      case 'error':
        return error || 'Error occurred'
      case 'disabled':
        return 'Connect wallet first'
      default:
        return 'Tap to speak'
    }
  }

  return (
    <div className={`flex flex-col items-center space-y-4 ${className}`}>
      {/* Voice Button */}
      <motion.button
        onClick={handleVoiceToggle}
        disabled={!isConnected}
        className="relative w-20 h-20 rounded-full glass border-2 border-white/30 flex items-center justify-center text-white transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/30"
        variants={buttonVariants}
        animate={buttonState}
        whileTap={{ scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        {getButtonIcon()}
        
        {/* Pulse animation for listening state */}
        <AnimatePresence>
          {isListening && (
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-red-400"
              initial={{ scale: 1, opacity: 1 }}
              animate={{ scale: 1.5, opacity: 0 }}
              exit={{ scale: 1, opacity: 0 }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
        </AnimatePresence>
      </motion.button>

      {/* Status Text */}
      <motion.p
        className="text-sm text-white/80 text-center font-medium"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {getStatusText()}
      </motion.p>

      {/* Waveform Animation */}
      <AnimatePresence>
        {showWaveform && (
          <motion.div
            className="flex items-center justify-center space-x-1 h-12"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="voice-wave"
                style={{
                  height: '8px',
                  animationDelay: `${i * 0.1}s`
                }}
                animate={{
                  height: isListening ? ['8px', '24px', '8px'] : '8px'
                }}
                transition={{
                  duration: 1.5,
                  repeat: isListening ? Infinity : 0,
                  ease: 'easeInOut'
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transcript Display */}
      <AnimatePresence>
        {transcript && (
          <motion.div
            className="max-w-md p-4 glass rounded-lg border border-white/20"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-white text-sm leading-relaxed">
              <span className="text-blue-300 font-medium">You said:</span>
              <br />
              "{transcript}"
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            className="max-w-md p-3 bg-red-500/20 border border-red-500/30 rounded-lg backdrop-blur-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-red-200 text-sm text-center">
              {error}
            </p>
            <button
              onClick={clearError}
              className="mt-2 w-full text-xs text-red-300 hover:text-red-100 transition-colors"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Text */}
      {!isConnected && (
        <motion.div
          className="max-w-md p-3 glass rounded-lg border border-white/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-white/70 text-xs text-center">
            Connect your wallet to start using voice commands
          </p>
        </motion.div>
      )}
    </div>
  )
}

export default VoiceInput